import { Analytics } from '@cobliteam/analytics';
import { useGetDevices } from '@cobliteam/hooks/src/useEntity/useDevices';
import useFuscaVehicle from '@cobliteam/hooks/src/useEntity/useFuscaVehicles';
import useVehicle from '@cobliteam/hooks/src/useEntity/useVehicle';
import { useTranslation } from '@cobliteam/i18n';
import Tag from '@cobliteam/ui/components/Tag';
import Typography from '@cobliteam/ui/components/Typography';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, useForm } from '@cobliteam/ui/form';
import Select, { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@cobliteam/ui/form/Select';
import Icon from '@cobliteam/ui/icons';
import { DialogSetupGuideShell } from '@cobliteam/ui-business/components/dialogs/DialogSetupGuideShell/DialogSetupGuideShell';
import { formatVehicleLabel } from '@cobliteam/ui-business/components/Selects/SelectVehicle/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { z } from 'zod';
import type { SetupGuideEntityFormProps } from '../entityModalConfig';
import SetupGuideFormRow from '../SetupGuideFormRow';
import { rowsForRetry, runSequentialSaves } from './sequentialSubmit';

/** Rows shown in the modal; only the first pair is required. */
const ROWS = 2;

/**
 * Minimal "associate vehicles and devices" modal — rows of (vehicle without device × unassigned
 * device). Options picked on one row are hidden from the others so a pair can't be reused.
 */
const DeviceAssociationForm = ({ fleetId, item, onClose, onSuccess }: SetupGuideEntityFormProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const { data: vehicles } = useVehicle().getVehicles({});
  const { data: devices } = useGetDevices({ includeLastLocation: false, includeLastMovingStateTime: false });
  const { mutateAsync: associateDevice, isPending } = useFuscaVehicle().associateDevice;

  const availableVehicles = useMemo(() => (vehicles ?? []).filter((vehicle) => !vehicle.deviceId), [vehicles]);
  const unassignedDevices = useMemo(() => (devices ?? []).filter((device) => !device.vehicle), [devices]);
  // No point rendering more rows than there are pairs left to associate.
  const rowCount = Math.max(1, Math.min(ROWS, availableVehicles.length, unassignedDevices.length));
  const hasPairs = availableVehicles.length > 0 && unassignedDevices.length > 0;

  const schema = useMemo(
    () =>
      z.object({
        rows: z.array(z.object({ vehicleId: z.string(), deviceId: z.string() })).superRefine((rows, ctx) => {
          rows.forEach((row, index) => {
            if (!(row.vehicleId || row.deviceId)) {
              // Only the first row is mandatory; later rows may stay empty.
              if (index === 0) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'vehicleId'], message: t('entity_modal.device-association.validation.pair_required') });
              return;
            }
            if (!row.vehicleId) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'vehicleId'], message: t('entity_modal.device-association.validation.pair_required') });
            if (!row.deviceId) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'deviceId'], message: t('entity_modal.device-association.validation.pair_required') });
          });
        }),
      }),
    [t]
  );

  type FormValues = z.infer<typeof schema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    // Always register every possible row (rowCount only limits rendering): the queries resolve after
    // mount, and a row that appears later without a registered default would flip its Selects from
    // uncontrolled to controlled.
    defaultValues: { rows: Array.from({ length: ROWS }, () => ({ vehicleId: '', deviceId: '' })) },
  });
  const selectedRows = form.watch('rows');

  const handleSubmit = async ({ rows }: FormValues) => {
    // Original indexes travel along so a partial failure can clear exactly the rows that saved.
    const filled = rows.map((row, rowIndex) => ({ row, rowIndex })).filter(({ row }) => row.vehicleId && row.deviceId);
    // Sequential associations; a failed row doesn't stop the following ones and the fusca hook toasts failures.
    const { succeeded, failed } = await runSequentialSaves(filled, ({ row }) => associateDevice({ vehicleId: row.vehicleId, deviceId: row.deviceId }));
    // Associated pairs exist even when another row fails, so the event reflects reality.
    if (succeeded.length > 0) Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'device-association', count: succeeded.length });
    if (failed.length > 0) {
      // Keep the form open for retry, dropping the rows that saved so retrying can't re-associate them.
      form.reset({ rows: rowsForRetry(rows, new Set(succeeded.map(({ rowIndex }) => rowIndex)), ROWS, () => ({ vehicleId: '', deviceId: '' })) });
      void form.trigger();
      return;
    }
    onSuccess(succeeded.length);
  };

  return (
    <DialogSetupGuideShell
      open
      onClose={onClose}
      title={t('entity_modal.device-association.title')}
      description={t('entity_modal.device-association.description')}
      primaryAction={{
        label: t('entity_modal.device-association.submit'),
        onClick: form.handleSubmit(handleSubmit),
        disabled: !(hasPairs && form.formState.isValid),
        loading: isPending,
        endIcon: <Icon name='check' size='sm' />,
        'data-testid': 'setup-guide-device-association-submit',
      }}
    >
      <Form form={form} onSubmit={handleSubmit}>
        <div className='flex flex-col'>
          <Tag color='default' size='sm' label={t('entity_modal.device-association.progress', { current: item.current, target: item.target })} />
          {hasPairs ? (
            Array.from({ length: rowCount }, (_, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size row list, never reordered
              <SetupGuideFormRow key={index} label={t('entity_modal.device-association.row_label', { index: index + 1 })}>
                <FormField control={form.control} name={`rows.${index}.vehicleId`}>
                  {({ field }) => (
                    <FormItem className='w-64'>
                      <FormLabel>{t('entity_modal.device-association.vehicle_label')}</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder={t('entity_modal.device-association.select_placeholder')} />
                          </SelectTrigger>
                          <SelectContent portal={false}>
                            {availableVehicles.map((vehicle) => (
                              <SelectItem key={vehicle.id} value={vehicle.id} disabled={selectedRows.some((row, rowIndex) => rowIndex !== index && row.vehicleId === vehicle.id)}>
                                {formatVehicleLabel(vehicle)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                </FormField>
                <FormField control={form.control} name={`rows.${index}.deviceId`}>
                  {({ field }) => (
                    <FormItem className='w-64'>
                      <FormLabel>{t('entity_modal.device-association.device_label')}</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder={t('entity_modal.device-association.select_placeholder')} />
                          </SelectTrigger>
                          <SelectContent portal={false}>
                            {unassignedDevices.map((device) => (
                              <SelectItem key={device.id} value={device.id} disabled={selectedRows.some((row, rowIndex) => rowIndex !== index && row.deviceId === device.id)}>
                                {device.cobliId}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                </FormField>
              </SetupGuideFormRow>
            ))
          ) : (
            <Typography variant='text-md' className='py-8 text-center text-gray-11'>
              {t('entity_modal.device-association.empty')}
            </Typography>
          )}
        </div>
      </Form>
    </DialogSetupGuideShell>
  );
};

export default DeviceAssociationForm;
